import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import { extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import {
  Minus, Plus, AlertTriangle, Check, X, Clock, TrendingDown,
  ChevronDown, ChevronUp, Trash2, RefreshCw, Shield, Sparkles,
  Coffee, Utensils, Home, Package, Zap, AlertCircle, MessageSquare,
  Gamepad2, Tv, Phone, Cookie, Star, Heart, Ban, Volume2, Settings
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format, subDays, isToday, isYesterday } from 'date-fns'

interface Deduction {
  id: string
  user_id: string
  username: string
  display_name: string
  deducted_by: string
  deducted_by_name: string
  points: number
  reason: string
  category: string
  deduction_date: string
  deduction_time: string
  acknowledged: boolean
  acknowledged_at?: string
  notes?: string
  created_at: string
}

interface Kid {
  id: string
  username: string
  display_name: string
  total_points: number
  recent_deductions?: number
  trend?: 'improving' | 'stable' | 'worsening'
}

interface QuickAction {
  reason: string
  points: number
  category: string
  icon: React.ReactNode
  color: string
  description?: string
}

// Will be loaded from database

const categoryConfig: { [key: string]: { icon: React.ReactNode, color: string, label: string } } = {
  dishes: { icon: <Utensils className="w-4 h-4" />, color: 'blue', label: 'Dishes' },
  trash: { icon: <Trash2 className="w-4 h-4" />, color: 'gray', label: 'Trash' },
  supplies: { icon: <Package className="w-4 h-4" />, color: 'yellow', label: 'Supplies' },
  cleanliness: { icon: <Home className="w-4 h-4" />, color: 'green', label: 'Cleanliness' },
  behavior: { icon: <Zap className="w-4 h-4" />, color: 'purple', label: 'Behavior' },
  screen: { icon: <Tv className="w-4 h-4" />, color: 'indigo', label: 'Screen Time' },
  food: { icon: <Cookie className="w-4 h-4" />, color: 'orange', label: 'Food' },
  other: { icon: <AlertCircle className="w-4 h-4" />, color: 'gray', label: 'Other' }
}

import { CommonDeductionsManager } from './CommonDeductionsManager'

export const EnhancedDeductionsManager: React.FC = () => {
  const { user } = useAuth()
  const [kids, setKids] = useState<Kid[]>([])
  const [recentDeductions, setRecentDeductions] = useState<Deduction[]>([])
  const [selectedKid, setSelectedKid] = useState<string>('')
  const [selectedAction, setSelectedAction] = useState<QuickAction | null>(null)
  const [customReason, setCustomReason] = useState('')
  const [points, setPoints] = useState<number>(5)
  const [notes, setNotes] = useState('')
  const [category, setCategory] = useState('other')
  const [showHistory, setShowHistory] = useState(false)
  const [loading, setLoading] = useState(false)
  const [historyFilter, setHistoryFilter] = useState<string>('all')
  const [timePeriod, setTimePeriod] = useState<string>('30') // days: 7, 14, 30, 90, 0 (all)
  const [showCustomForm, setShowCustomForm] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [quickActions, setQuickActions] = useState<QuickAction[]>([])

  useEffect(() => {
    if (user?.is_parent) {
      loadData()
    }
  }, [user])

  // Reload deductions when time period changes
  useEffect(() => {
    if (user?.is_parent && kids.length > 0) {
      loadDeductions()
    }
  }, [timePeriod])

  const loadDeductions = async () => {
    try {
      const recent = await apiClient.get(`/api/deductions/recent?days=${timePeriod}`)
      setRecentDeductions(recent || [])
    } catch (error) {
      console.error('Failed to load deductions:', error)
    }
  }

  const loadData = async () => {
    try {
      // Load kids with their point totals - use the parent/children endpoint
      const kidsResponse = await apiClient.get('/api/parent/children')
      const kidsList = Array.isArray(kidsResponse) ? kidsResponse : (kidsResponse?.children || [])

      // Load common deductions from database
      const commonDeductionsResponse = await apiClient.get('/api/deductions/common')
      const commonDeductions = Array.isArray(commonDeductionsResponse) ? commonDeductionsResponse : (commonDeductionsResponse?.deductions || [])
      const mappedActions = commonDeductions.map((d: any) => ({
        reason: d.reason,
        points: d.points,
        category: d.category || 'other',
        icon: categoryConfig[d.category]?.icon || <AlertCircle className="w-4 h-4" />,
        color: categoryConfig[d.category]?.color || 'gray',
        description: categoryConfig[d.category]?.label || d.category
      }))
      setQuickActions(mappedActions)

      // Load deductions with time period filter
      const recent = await apiClient.get(`/api/deductions/recent?days=${timePeriod}`)
      setRecentDeductions(recent || [])

      // Calculate trends for each kid
      const enhancedKids = kidsList.map((kid: Kid) => {
        const kidDeductions = recent.filter((d: Deduction) => d.user_id === kid.id)
        const recentCount = kidDeductions.filter((d: Deduction) =>
          new Date(d.deduction_date + 'T12:00:00') > subDays(new Date(), 7)
        ).length
        const olderCount = kidDeductions.filter((d: Deduction) =>
          new Date(d.deduction_date + 'T12:00:00') <= subDays(new Date(), 7) &&
          new Date(d.deduction_date + 'T12:00:00') > subDays(new Date(), 14)
        ).length
        
        let trend: 'improving' | 'stable' | 'worsening' = 'stable'
        if (recentCount < olderCount) trend = 'improving'
        if (recentCount > olderCount) trend = 'worsening'
        
        return {
          ...kid,
          recent_deductions: recentCount,
          trend
        }
      })
      
      setKids(enhancedKids)
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load deductions data')
    }
  }

  const handleQuickAction = (action: QuickAction) => {
    setSelectedAction(action)
    setCustomReason(action.reason)
    setPoints(action.points)
    setCategory(action.category)
    setShowCustomForm(false)
  }

  const handleDeduct = async () => {
    if (!selectedKid) {
      toast.error('Please select a child')
      return
    }
    
    const finalReason = customReason.trim() || selectedAction?.reason
    if (!finalReason) {
      toast.error('Please provide a reason')
      return
    }
    
    if (points <= 0) {
      toast.error('Points must be greater than 0')
      return
    }
    
    setLoading(true)
    try {
      const result = await apiClient.post('/api/deductions/deduct', {
        user_id: selectedKid,
        points,
        reason: finalReason,
        category: category || selectedAction?.category || 'other',
        notes,
        deduction_date: extractDateInTimezoneSync(new Date().toISOString()),
        deduction_time: new Date().toTimeString().split(' ')[0]
      })
      
      const kid = kids.find(k => k.id === selectedKid)
      toast.success(`Deducted ${points} points from ${kid?.display_name}`, {
        icon: '⚠️',
        duration: 4000
      })
      
      // Reset form
      setSelectedKid('')
      setSelectedAction(null)
      setCustomReason('')
      setPoints(5)
      setNotes('')
      setCategory('other')
      setShowCustomForm(false)
      
      // Reload data
      await loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to deduct points')
    } finally {
      setLoading(false)
    }
  }

  const handleReverseDeduction = async (deduction: Deduction) => {
    try {
      await apiClient.delete(`/api/deductions/${deduction.id}`)
      toast.success(`Reversed ${deduction.points} points for ${deduction.display_name}`, {
        icon: '✅'
      })
      await loadData()
    } catch (error) {
      toast.error('Failed to reverse deduction')
    }
  }

  const formatDeductionTime = (date: string, time: string) => {
    const deductionDate = new Date(date + 'T12:00:00')
    if (isToday(deductionDate)) return `Today at ${time.slice(0, 5)}`
    if (isYesterday(deductionDate)) return `Yesterday at ${time.slice(0, 5)}`
    return format(deductionDate, 'MMM d') + ` at ${time.slice(0, 5)}`
  }

  const getTrendIcon = (trend?: string) => {
    if (trend === 'improving') return <TrendingDown className="w-4 h-4 text-green-500 rotate-180" />
    if (trend === 'worsening') return <TrendingDown className="w-4 h-4 text-red-500" />
    return <Minus className="w-3 h-3 text-gray-400" />
  }

  const filteredHistory = historyFilter === 'all' 
    ? recentDeductions 
    : recentDeductions.filter(d => d.user_id === historyFilter)

  if (!user?.is_parent) {
    return null
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <Shield className="w-7 h-7 mr-3" />
              Point Deductions & Discipline
            </h1>
            <p className="mt-1 opacity-90">Manage behavior consequences and track improvements</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              title="Manage Common Deductions"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={() => loadData()}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Common Deductions Settings - shown right below header when toggled */}
      {showSettings && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Manage Common Deductions</h2>
            <button
              onClick={() => setShowSettings(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <CommonDeductionsManager />
        </div>
      )}

      {/* Kid Selection with Stats */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Select Child</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kids.map(kid => (
            <button
              key={kid.id}
              onClick={() => setSelectedKid(kid.id)}
              className={`relative p-4 rounded-xl border-2 transition-all ${
                selectedKid === kid.id
                  ? 'border-red-500 bg-red-50 shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {/* Trend Indicator */}
              <div className="absolute top-2 right-2">
                {getTrendIcon(kid.trend)}
              </div>
              
              <div className="text-left">
                <div className="font-semibold text-lg">{kid.display_name}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-2xl font-bold text-gray-800">{kid.total_points}</span>
                  <span className="text-xs text-gray-500">points</span>
                </div>
                {kid.recent_deductions !== undefined && (
                  <div className="text-xs text-gray-500 mt-1">
                    {kid.recent_deductions} deductions this week
                  </div>
                )}
              </div>
              
              {selectedKid === kid.id && (
                <div className="absolute -top-2 -right-2">
                  <div className="bg-red-500 text-white rounded-full p-1">
                    <Check className="w-3 h-3" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      {selectedKid && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Quick Actions</h2>
            <button
              onClick={() => {
                setShowCustomForm(!showCustomForm)
                setSelectedAction(null)
              }}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                showCustomForm
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Custom Deduction
            </button>
          </div>

          {!showCustomForm ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    selectedAction?.reason === action.reason
                      ? `border-${action.color}-500 bg-${action.color}-50 shadow-md`
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`p-1.5 rounded bg-${action.color}-100 text-${action.color}-600`}>
                          {action.icon}
                        </div>
                        <span className="font-medium text-sm">{action.reason}</span>
                      </div>
                      {action.description && (
                        <p className="text-xs text-gray-500 ml-8">{action.description}</p>
                      )}
                    </div>
                    <div className="text-lg font-bold text-red-600">-{action.points}</div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4 bg-gray-50 rounded-lg p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Deduction
                </label>
                <input
                  type="text"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter reason..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points to Deduct
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPoints(Math.max(1, points - 5))}
                      className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={points}
                      onChange={(e) => setPoints(Math.max(1, parseInt(e.target.value) || 0))}
                      className="w-20 text-center px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                    <button
                      onClick={() => setPoints(points + 5)}
                      className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    {Object.entries(categoryConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add context or details..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          )}

          {/* Deduct Button */}
          {(selectedAction || showCustomForm) && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Deducting: </span>
                <span className="text-red-600 font-bold">{points} points</span>
                {selectedAction && !showCustomForm && (
                  <span> for "{selectedAction.reason}"</span>
                )}
              </div>
              <button
                onClick={handleDeduct}
                disabled={loading}
                className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Minus className="w-4 h-4" />
                    Deduct Points
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Recent History */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Recent Deductions
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 3 months</option>
              <option value="0">All time</option>
            </select>
            <select
              value={historyFilter}
              onChange={(e) => setHistoryFilter(e.target.value)}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="all">All Kids</option>
              {kids.map(kid => (
                <option key={kid.id} value={kid.id}>{kid.display_name}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No recent deductions</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredHistory.slice(0, 10).map(deduction => {
              const config = categoryConfig[deduction.category] || categoryConfig.other
              return (
                <div
                  key={deduction.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${config.color}-100 text-${config.color}-600`}>
                      {config.icon}
                    </div>
                    <div>
                      <div className="font-medium">{deduction.reason}</div>
                      <div className="text-sm text-gray-500">
                        {deduction.display_name} • {formatDeductionTime(deduction.deduction_date, deduction.deduction_time)}
                      </div>
                      {deduction.notes && (
                        <div className="text-xs text-gray-400 mt-1">{deduction.notes}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-red-600">-{deduction.points}</span>
                    {!deduction.acknowledged && (
                      <button
                        onClick={() => handleReverseDeduction(deduction)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Reverse deduction"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

    </div>
  )
}